<template>
  <div class="offcanvas offcanvas-end" style="width: min(720px, 100vw);" id="OffcanvasSettlement" ref="offcanvas" data-bs-backdrop="static">
    <div class="bg-body-secondary border offcanvas-header py-2">
      <span>
        <span class="badge me-2" :style="{ backgroundColor: platform.color || '#6B7280' }">{{ platform.name }}</span>
        {{ $t('_delivery.Receive platform payout') }}
      </span>
      <button type="button" style="font-size: small;" class="btn-close shadow-none text-reset" data-bs-dismiss="offcanvas" aria-label="Close" />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div class="offcanvas-body p-0" v-else>

      <!-- เลือกสาขา — เงินจาก platform เข้าแยกรายสาขา (แต่ละสาขา = store แยกบน platform) -->
      <div class="border-bottom px-3 py-2 d-flex align-items-center gap-2 bg-light">
        <label class="form-label mb-0 small fw-bold"><i class="fa-solid fa-store me-1"/>{{ $t('_delivery.Branch') }}</label>
        <select class="form-select form-select-sm w-auto shadow-none" v-model="branchId" @change="onBranchChange">
          <option :value="null">{{ $t('_delivery.All branches') }}</option>
          <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
        </select>
      </div>

      <!-- ===== บิลรอเคลียร์ ===== -->
      <div class="border-bottom px-3 py-2 fw-bold">
        <i class="fa-solid fa-hourglass-half me-2 text-warning"/>{{ $t('_delivery.Bills awaiting payout') }}
        <span class="badge rounded-pill bg-secondary ms-1">{{ pending.bills.length }}</span>
      </div>

      <div v-if="!pending.bills.length" class="text-center text-muted py-4">
        {{ $t('_delivery.No bills awaiting payout') }}
      </div>
      <template v-else>
        <table class="table table-sm align-middle mb-0">
          <thead>
            <tr class="text-muted small">
              <th class="ps-3" style="width: 36px;">
                <input type="checkbox" class="form-check-input" :checked="allSelected" @change="toggleAll($event.target.checked)">
              </th>
              <th>{{ $t('_delivery.Bill') }}</th>
              <th>{{ $t('_delivery.Order ref') }}</th>
              <th class="text-end">{{ $t('_delivery.Gross') }}</th>
              <th class="text-end">{{ $t('_delivery.GP') }}</th>
              <th class="text-end pe-3">{{ $t('_delivery.Expected') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in pending.bills" :key="b.id" class="cursor-pointer" @click="toggle(b.id)">
              <td class="ps-3">
                <input type="checkbox" class="form-check-input" :checked="selected.includes(b.id)" @click.stop="toggle(b.id)">
              </td>
              <td>
                <div>{{ b.bill_no }}</div>
                <div class="text-muted" style="font-size: 11px;">
                  {{ formatDate(b.closed_at) }}<span v-if="!branchId && b.branch_name"> · {{ b.branch_name }}</span>
                </div>
              </td>
              <td>
                {{ b.external_order_ref || '-' }}
                <i v-if="b.is_pickup" class="fa-solid fa-person-walking text-muted ms-1" :title="$t('_delivery.Customer self pickup')"/>
              </td>
              <td class="text-end">{{ $storeIndex.formatThaiCurrency(b.total) }}</td>
              <td class="text-end text-danger">-{{ $storeIndex.formatThaiCurrency(b.platform_commission) }}</td>
              <td class="text-end pe-3 fw-bold">{{ $storeIndex.formatThaiCurrency(b.expected_net_payout) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- ฟอร์มบันทึกรอบรับเงิน -->
        <div class="border-top bg-light px-3 py-3">
          <div class="d-flex flex-wrap align-items-end gap-3">
            <div>
              <label class="form-label mb-0 small fw-bold">{{ $t('_delivery.Payout date') }}</label>
              <input type="date" class="form-control form-control-sm shadow-none" v-model="form.settled_date">
            </div>
            <div>
              <label class="form-label mb-0 small fw-bold">{{ $t('_delivery.Amount received in bank') }}</label>
              <input type="number" min="0" step="0.01" class="form-control form-control-sm text-end shadow-none" style="width: 140px;" v-model="form.received_amount">
            </div>
            <div class="flex-grow-1">
              <label class="form-label mb-0 small fw-bold">{{ $t('Note') }}</label>
              <input type="text" class="form-control form-control-sm shadow-none" v-model.trim="form.note" :placeholder="$t('optional')">
            </div>
          </div>
          <div class="d-flex align-items-center mt-2">
            <div class="small">
              {{ $t('_delivery.Selected') }} <b>{{ selected.length }}</b> {{ $t('_delivery.bills') }}
              · {{ $t('_delivery.Expected') }} <b>{{ $storeIndex.formatThaiCurrency(selectedExpected) }}</b>
              <template v-if="form.received_amount !== '' && difference !== 0">
                · <span :class="difference < 0 ? 'text-danger' : 'text-success'">
                  {{ $t('_delivery.Difference') }} {{ $storeIndex.formatThaiCurrency(difference) }}
                </span>
              </template>
            </div>
            <ladda
              class="ms-auto"
              :text="$t('_delivery.Confirm payout received')"
              :btn-class="'btn btn-primary btn-sm'"
              :loading="loadingSave"
              icon="<i class='fa-solid fa-hand-holding-dollar'></i>"
              :disabled="!canSave"
              @click="handleSave"
            />
          </div>
        </div>
      </template>

      <!-- ===== ประวัติรอบรับเงิน ===== -->
      <div class="border-bottom border-top px-3 py-2 fw-bold mt-2">
        <i class="fa-solid fa-clock-rotate-left me-2 text-secondary"/>{{ $t('_delivery.Payout history') }}
      </div>
      <div v-if="!history.length" class="text-center text-muted py-4">
        {{ $t('_delivery.No payouts recorded yet') }}
      </div>
      <table v-else class="table table-sm align-middle mb-0">
        <thead>
          <tr class="text-muted small">
            <th class="ps-3">{{ $t('_delivery.Payout date') }}</th>
            <th class="text-end">{{ $t('_delivery.Expected') }}</th>
            <th class="text-end">{{ $t('_delivery.Received') }}</th>
            <th class="text-end">{{ $t('_delivery.Difference') }}</th>
            <th>{{ $t('Note') }}</th>
            <th style="width: 48px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="st in history" :key="st.id">
            <td class="ps-3">
              <div>{{ st.settled_date }}</div>
              <div class="text-muted" style="font-size: 11px;">{{ st.bill_count }} {{ $t('_delivery.bills') }}</div>
            </td>
            <td class="text-end">{{ $storeIndex.formatThaiCurrency(st.expected_amount) }}</td>
            <td class="text-end fw-bold">{{ $storeIndex.formatThaiCurrency(st.received_amount) }}</td>
            <td class="text-end" :class="Number(st.difference) < 0 ? 'text-danger' : (Number(st.difference) > 0 ? 'text-success' : 'text-muted')">
              {{ $storeIndex.formatThaiCurrency(st.difference) }}
            </td>
            <td class="text-truncate" style="max-width: 140px;">{{ st.note || '-' }}</td>
            <td class="text-end pe-2">
              <button type="button" class="btn btn-outline-danger btn-sm btn-icon" @click="handleVoid(st)">
                <i class="fa-solid fa-rotate-left"/>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { DeliveryPlatforms } from '@/services/endpoint/delivery-platforms.service';
import moment from 'moment';

/**
 * P2 รับเงิน platform (docs ฝั่ง api: delivery-platform-pricing.md §9)
 * - ติ๊กบิล platform_pending ตามที่รอบโอนครอบ → กรอกยอดเงินเข้าจริง → ยืนยัน
 *   = สร้างรอบรับเงิน 1 record + บิลชุด flip เป็น paid (expected คำนวณฝั่ง server)
 * - ส่วนต่างเก็บที่รอบ (โดนหักเกิน/ปรับยอด) — ไม่แตะยอดบิล
 * - ยกเลิกรอบ = บิลถอยกลับ platform_pending (บันทึกผิด)
 */
export default {
  props: {
    platform: { type: Object, required: true },
  },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      branchId: null,
      branches: [],
      pending: { bills: [], summary: null },
      history: [],
      selected: [],
      form: {
        settled_date: moment().format('YYYY-MM-DD'),
        received_amount: '',
        note: ''
      }
    }
  },
  components: { PlaceholderContent, Ladda, AccessDenied },
  computed: {
    allSelected() {
      return this.pending.bills.length > 0 && this.selected.length === this.pending.bills.length;
    },
    selectedExpected() {
      return this.pending.bills
        .filter(b => this.selected.includes(b.id))
        .reduce((sum, b) => sum + Number(b.expected_net_payout || 0), 0);
    },
    difference() {
      return Math.round((Number(this.form.received_amount || 0) - this.selectedExpected) * 100) / 100;
    },
    canSave() {
      return this.selected.length > 0
        && this.form.settled_date
        && this.form.received_amount !== ''
        && Number(this.form.received_amount) >= 0;
    }
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format('DD/MM HH:mm') : '';
    },
    toggle(id) {
      const index = this.selected.indexOf(id);
      if (index === -1) this.selected.push(id);
      else this.selected.splice(index, 1);
    },
    toggleAll(checked) {
      this.selected = checked ? this.pending.bills.map(b => b.id) : [];
    },
    onBranchChange() {
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        // เงินเข้าแยกรายสาขา → filter pending + history ตามสาขาที่เลือก
        const scope = this.branchId ? { branch_id: this.branchId } : {};
        const [pendingRes, historyRes] = await Promise.all([
          DeliveryPlatforms.settlementPending(this.platform.id, '?' + $.param(scope)),
          DeliveryPlatforms.settlements(this.platform.id, '?' + $.param({ ...scope, limit: 10, page: 1 }))
        ]);
        this.pending.bills = pendingRes.data.data?.bills ?? [];
        this.pending.summary = pendingRes.data.data?.summary ?? null;
        this.branches = pendingRes.data.branches ?? this.branches;
        this.history = historyRes.data.data ?? [];
        // default: เลือกทุกบิล (เคสปกติ = รอบโอนครอบทั้งหมดที่ค้าง)
        this.selected = this.pending.bills.map(b => b.id);
        this.hasPermission = true;
      } catch (err) {
        if (err?.response?.status === 403) this.hasPermission = false;
        else if (err?.response?.data?.message) this.$toast.warn(this.$t(err?.response?.data?.message));
      } finally {
        this.loading = false;
      }
    },
    handleSave() {
      if (!this.canSave || this.loadingSave) return;
      const self = this;
      const diffLine = this.difference !== 0
        ? '<br><span class="' + (this.difference < 0 ? 'text-danger' : 'text-success') + '">'
          + this.$t('_delivery.Difference') + ' ' + this.$storeIndex.formatThaiCurrency(this.difference) + '</span>'
        : '';
      this.$swal({
        icon: 'question',
        iconColor: '#FF7043',
        title: this.$t('_delivery.Confirm payout received') + '?',
        html: this.$t('_delivery.Selected') + ' ' + this.selected.length + ' ' + this.$t('_delivery.bills')
          + ' · ' + this.$t('_delivery.Expected') + ' ' + this.$storeIndex.formatThaiCurrency(this.selectedExpected)
          + ' · ' + this.$t('_delivery.Received') + ' ' + this.$storeIndex.formatThaiCurrency(Number(this.form.received_amount))
          + diffLine,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t('Confirm'),
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-primary me-2', cancelButton: 'btn btn-danger' },
        focusConfirm: false,
        showLoaderOnConfirm: true,
        preConfirm: async function () {
          return DeliveryPlatforms.createSettlement(self.platform.id, {
            branch_id: self.branchId,
            settled_date: self.form.settled_date,
            received_amount: Number(self.form.received_amount),
            note: self.form.note || null,
            bill_ids: self.selected
          }).then(() => {
            self.$toast.success('<b>' + self.$t('Success') + '</b> ' + self.$t('_delivery.Payout recorded'));
            self.form.received_amount = '';
            self.form.note = '';
            self.fetchData();
          }).catch((error) => {
            if (error?.response?.data?.message) {
              self.$swal.showValidationMessage(error.response.data.message);
            }
          });
        }
      });
    },
    handleVoid(settlement) {
      const self = this;
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('_delivery.Void this payout record') + '?',
        html: this.$t('_delivery.Bills in this payout go back to awaiting payout'),
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t('Confirm'),
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-primary me-2', cancelButton: 'btn btn-danger' },
        focusConfirm: false,
        showLoaderOnConfirm: true,
        preConfirm: async function () {
          return DeliveryPlatforms.voidSettlement(self.platform.id, settlement.id)
            .then(() => {
              self.$toast.success('<b>' + self.$t('Success') + '</b> ' + self.$t('_delivery.Payout record voided'));
              self.fetchData();
            })
            .catch((error) => {
              if (error?.response?.data?.message) {
                self.$swal.showValidationMessage(error.response.data.message);
              }
            });
        }
      });
    }
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('shown.bs.offcanvas', () => this.fetchData());
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.settlement.open = false;
    });
  }
}
</script>
